import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { APPOINTMENTS_CHANGED, appointmentService } from '../../services/appointmentService';
import { useLanguage } from '../../hooks/useLanguage';
import { AppointmentViewer } from './AppointmentViewer';
import { Appointment, DayState, dayStates, interventionLabel, todayKey } from './appointmentUtils';

const chip: Record<DayState, string> = {
    done: 'bg-gray-100 text-gray-500',
    ongoing: 'bg-green-100 text-green-700',
    next: 'bg-indigo-600 text-white',
    upcoming: 'bg-white text-gray-500 border border-gray-200'
};

// Pastille de l'en-tête : « Aujourd'hui · N RDV ». Le détail du jour (état de chaque rendez-vous) s'ouvre
// en menu au clic, pour ne jamais recouvrir les boutons d'action des pages (Envoyer, Enregistrer…).
export const TodayAppointmentsWidget: React.FC = () => {
    const { t, isRTL } = useLanguage();
    const navigate = useNavigate();
    const [items, setItems] = useState<Appointment[]>([]);
    const [pending, setPending] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const [selected, setSelected] = useState<Appointment | null>(null);

    const load = useCallback(async () => {
        try {
            const [day, stats] = await Promise.all([
                appointmentService.getAppointments({ date: todayKey(), status: 'CONFIRMED,COMPLETED', limit: 50 }),
                appointmentService.getStats()
            ]);
            setItems((day.data || []).sort((a: Appointment, b: Appointment) => a.timeSlot.localeCompare(b.timeSlot)));
            setPending(stats.data?.awaitingGarage || 0);
        } catch {
            // le widget ne doit jamais gêner le dashboard
        } finally {
            setLoaded(true);
        }
    }, []);

    // Chargé à l'ouverture de la page, puis seulement après une action sur un rendez-vous
    // (pas d'interrogation périodique : elle multipliait les appels serveur).
    useEffect(() => {
        load();
        window.addEventListener(APPOINTMENTS_CHANGED, load);
        return () => window.removeEventListener(APPOINTMENTS_CHANGED, load);
    }, [load]);

    // Fermeture du menu : clic à l'extérieur ou touche Échap
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (!loaded) return null;
    const states = dayStates(items);

    return (
        <>
            <div ref={wrapRef} className="relative">
                <button
                    onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                    aria-haspopup="true"
                    aria-label={t('appointments.widget.title', { count: items.length })}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-900 hover:bg-gray-50"
                >
                    <CalendarDays size={16} className="text-indigo-600" />
                    <span className="hidden md:inline">{t('appointments.widget.title', { count: items.length })}</span>
                    <span className="md:hidden">{items.length}</span>
                    {pending > 0 && <span className="w-2 h-2 rounded-full bg-amber-500" aria-hidden="true" />}
                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                    <div className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} z-50 w-[23rem] max-w-[calc(100vw-1.5rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden`} dir={isRTL ? 'rtl' : 'ltr'}>
                        <div className="flex items-center justify-between gap-2 px-4 py-3">
                            <span className="font-bold text-gray-900 text-sm">{t('appointments.widget.title', { count: items.length })}</span>
                            <button onClick={() => { setOpen(false); navigate('/dashboard/appointments'); }} className="text-sm font-semibold text-indigo-600 hover:underline">{t('appointments.widget.agenda')}</button>
                        </div>
                        {pending > 0 && (
                            <button onClick={() => { setOpen(false); navigate('/dashboard/appointments'); }} className="w-full text-start px-4 py-2 text-xs font-semibold text-amber-800 bg-amber-50 border-y border-amber-100 hover:bg-amber-100">
                                {t('appointments.widget.toValidate', { count: pending })}
                            </button>
                        )}
                        {items.length === 0 ? (
                            <p className="px-4 pb-4 pt-1 text-sm text-gray-500">{t('appointments.widget.empty')}</p>
                        ) : (
                            <ul className="max-h-72 overflow-y-auto border-t border-gray-100 divide-y divide-gray-100">
                                {items.map(a => {
                                    const st = states[a._id];
                                    return (
                                        <li key={a._id}>
                                            <button onClick={() => { setOpen(false); setSelected(a); }} className={`w-full text-start px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 ${st === 'next' ? 'bg-indigo-50' : ''}`}>
                                                <span className={`w-11 text-sm font-bold ${st === 'next' ? 'text-indigo-600' : 'text-gray-500'}`}>{a.timeSlot}</span>
                                                <span className="flex-1 min-w-0">
                                                    <span className="block text-sm font-bold text-gray-900 truncate">{a.clientName}{a.vehicleModel ? ` · ${a.vehicleModel}` : ''}</span>
                                                    <span className="block text-xs text-gray-500 truncate">{interventionLabel(a, t(`appointments.type.${a.type}`, { defaultValue: a.type }))}</span>
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${chip[st]}`}>{t(`appointments.widget.state.${st}`)}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                )}
            </div>
            {selected && <AppointmentViewer appointment={selected} onClose={() => setSelected(null)} onChanged={load} />}
        </>
    );
};
